/* Frontier fire pit configurator (fdp). One instance per section; no globals besides the element definition. */
(() => {
  if (customElements.get('fdp-configurator')) return;

  const money = (n) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: n % 1 ? 2 : 0
  }).format(n || 0);
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const ICONS = {
    flame: '<svg class="burner-flame" viewBox="0 0 24 24" aria-hidden="true"><path d="M13 2c1 5-4 6-4 10 0 1 .4 2 1 3-3-1-4-3-4-5-3 3-3 6-2 8 1.5 3 4.3 4 8 4 5 0 8-3 8-7 0-5-3-8-5-10 0 3-1 4-2 5 1-3 1-5 0-8Z"/></svg>',
    match: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 21 8-8M4 19l2 2"/><path d="M14 4c1 4-3 4-3 7a4 4 0 0 0 8 0c0-3-2-5-3-7 0 2-1 3-2 4 1-2 0-3 0-4Z"/></svg>',
    spark: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M13 2 4 14h7l-1 8 10-13h-7l1-7Z"/></svg>',
    wifi: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 8a16 16 0 0 1 20 0M5 12a11 11 0 0 1 14 0M8 16a6 6 0 0 1 8 0"/><circle cx="12" cy="20" r="1"/></svg>'
  };
  /* Need-to-know details for each accessory (from the old accessory product pages), shown in a pop-up */
  const ACC_DETAILS = {
    lid_corten: ['Fitted 14-gauge Corten steel snuffer lid with handles, sized to your pit.', 'Snuffs the fire safely and keeps rain and debris out between uses.', 'Doubles as a tabletop when the pit is cool.', 'Weathers to the same patina as your fire pit.'],
    lid_stainless: ['Premium 14-gauge stainless steel snuffer lid with handles.', 'Snuffs the fire safely and keeps rain, debris and moisture out.', 'Doubles as a tabletop when the pit is cool.', 'Clean, contrasting finish against the Corten.'],
    grate: ['Heavy-duty 3/4-inch welded steel bar, powder coated.', 'Raises logs off the base for better airflow: hotter flames, less smoke, a cleaner, longer burn.', 'Reinforced frame with cross bars, sized to each Frontier pit.', 'Wood-burning only.'],
    cover: ['Fitted cover made for each Frontier model and size.', 'Keeps out rain, dust and debris between gatherings.', 'Always works better with a steel lid underneath.'],
    deck: ['Three-layer heat barrier: silicone-coated fiberglass, fiberglass insulation and an aluminum-foil fiberglass outer layer.', 'Withstands up to 1000°F; protects decks, patios, lawns and stone from heat, sparks and embers.', 'Reinforced silicone-tape edging for long-term use.', 'Sized to your pit, 1mm thick.'],
    firestarter: ['All-natural wood shavings dipped in food-safe wax.', 'Lights fast with a strong, steady flame and no lighter-fluid smell.', 'One bundle is good for up to 20 fires.']
  };
  const ACC_ORDER = ['lid_corten', 'lid_stainless', 'grate', 'cover', 'deck', 'firestarter'];
  const LIDS = ['lid_corten', 'lid_stainless'];
  const KINDS = ['fuel', 'size', 'burner', 'ignition', 'media', 'logs', 'logstyle'];

  class FdpConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.cfg = JSON.parse(this.querySelector('[data-fdp-config]').textContent);
      this.product = JSON.parse(this.querySelector('[data-fdp-product]').textContent);
      this.tradeRate = parseFloat(this.dataset.tradeRate) || 0;
      this.phone = this.dataset.phone || '';
      this.resolved = {};
      this.pending = false;
      this.state = { fuel: 'wood', size: this.cfg.sizes[0].key, burner: null, ignition: null, media: null, logs: null, logstyle: null, acc: new Set() };
      // Arriving from the "Save $250" bundles page (?kit=1): start with the complete kit (Corten lid + grate + cover) selected.
      try {
        if (this.cfg.kit && new URLSearchParams(location.search).get('kit') === '1') {
          ['lid_corten', 'grate', 'cover'].forEach((k) => { if (this.cfg.accessory_info && this.cfg.accessory_info[k]) this.state.acc.add(k); });
        }
      } catch (e) {}
      this.cleanups = [];
      this.$ = (sel) => this.querySelector(sel);
      this.$$ = (sel) => [...this.querySelectorAll(sel)];
      this.buildStatic();
      this.bind();
      this.setupLayout();
      this.preloadGallery();
      this.setupThumbStrip();
      this.setupPhotoSwipe();
      this.render();
      this.resolveProducts().then(() => { this.updateFromPrices(); this.render(); });
    }

    disconnectedCallback() { this.cleanups.forEach((fn) => fn()); this.cleanups = []; this._ready = false; }

    on(target, type, fn, opts) { target.addEventListener(type, fn, opts); this.cleanups.push(() => target.removeEventListener(type, fn, opts)); }

    /* ---------- data ---------- */
    size() { return this.cfg.sizes.find((s) => s.key === this.state.size) || this.cfg.sizes[0]; }

    async resolveProducts() {
      const handles = new Set();
      const walk = (o) => {
        if (!o || typeof o !== 'object') return;
        if (typeof o.product === 'string' && o.product) handles.add(o.product);
        Object.values(o).forEach(walk);
      };
      walk(this.cfg);
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      await Promise.all([...handles].map(async (h) => {
        try {
          const r = await fetch(`${root}products/${encodeURIComponent(h)}.js`);
          if (r.ok) this.resolved[h] = await r.json();
        } catch (e) { /* stays unmapped */ }
      }));
    }

    /** {price, id, available, mapped} for a config entry. Vessel entries with no product handle use this page's product. */
    item(entry, variantTitle) {
      if (!entry) return { price: null, id: null, available: false, mapped: false };
      const handle = entry.product;
      const product = handle ? this.resolved[handle] : (entry.isVessel ? this.product : null);
      const want = (variantTitle ?? entry.variant ?? '').toString().trim().toLowerCase();
      let variant = null;
      if (product && product.variants) {
        if (want) variant = product.variants.find((v) => v.title.trim().toLowerCase() === want);
        else if (handle && product.variants.length === 1) variant = product.variants[0];
      }
      if (variant) return { price: variant.price / 100, id: variant.id, available: variant.available, mapped: true };
      return { price: entry.price ?? null, id: null, available: false, mapped: false };
    }

    vessel(fuel = this.state.fuel, size = this.size()) { return this.item({ ...size.vessel[fuel], isVessel: true }); }
    accessory(key, size = this.size()) { return this.item(size.accessories[key]); }
    /** Accessories this model offers (e.g. the Pierson has no lids or grate add-on). */
    accKeys() { return ACC_ORDER.filter((k) => this.cfg.accessory_info[k] && this.size().accessories[k]); }
    burnerEntry() { return this.size().burners.find((b) => b.label === this.state.burner); }
    ignitionEntry() { return this.cfg.ignition.find((i) => i.label === this.state.ignition); }
    mediaEntry() { return this.cfg.media.find((m) => m.label === this.state.media); }

    kitEligible() {
      const a = this.state.acc;
      if (!this.cfg.kit) return false;
      return this.state.fuel === 'wood' && LIDS.some((k) => a.has(k)) && a.has('grate') && a.has('cover');
    }

    lines() {
      const s = this.state, size = this.size(), gas = s.fuel === 'gas';
      const rows = [];
      const push = (key, label, qty, it, extra = {}) => rows.push({ key, label, qty, unit: it.price, id: it.id, mapped: it.mapped, available: it.available, ...extra });
      push('vessel', `${this.cfg.model} ${size.key} ${gas ? 'Gas vessel' : 'Wood-Burning'}`, 1, this.vessel(), { vessel: true });
      if (gas) {
        const b = this.burnerEntry();
        if (b) push('burner', `${b.label} CrossFire 2.0 burner`, 1, this.item(b));
        const ig = this.ignitionEntry();
        if (ig) {
          if (ig.price === 0 && !ig.product) rows.push({ key: 'ignition', label: ig.label, qty: 1, unit: 0, id: null, mapped: true, available: true, included: true });
          else push('ignition', ig.label, 1, this.item(ig));
        }
        const m = this.mediaEntry();
        if (m) push('media', `${m.label} (${m.bag})`, size.media_bags, this.item(m));
        if (s.logs === 'ceramic') {
          const c = this.cfg.ceramic, style = c.styles.find((x) => x.label === s.logstyle);
          push('logs', `${c.label}${style ? ' · ' + style.label : ''}`, size.ceramic_sets,
            this.item({ product: c.product, variant: style ? style.variant : '', price: c.price }));
        }
        if (s.logs === 'steel') push('logs', 'Steel Welded Log Set', 1, this.item(size.steel_logs));
      }
      (this.cfg.included || []).forEach((inc) => { if (!(gas && inc.wood_only)) rows.push({ key: 'included', label: inc.label, qty: 1, unit: 0, id: null, mapped: true, available: true, included: true }); });
      this.accKeys().forEach((k) => {
        if (!s.acc.has(k)) return;
        if (gas && this.cfg.accessory_info[k].wood_only) return;
        push(k, this.cfg.accessory_info[k].label, 1, this.accessory(k));
      });
      return rows;
    }

    pricing(rows) {
      const subtotal = rows.reduce((sum, r) => sum + (r.unit || 0) * r.qty, 0);
      const kit = this.kitEligible() ? this.cfg.kit.discount : 0;
      // Trade discount applies to Frontier Designs products only (vessel, lids, grate, cover, deck protector, fire starter…),
      // never to Warming Trends parts: burners, ignition, fire media and log sets.
      const WT = ['burner', 'ignition', 'media', 'logs'];
      const tradeBase = rows.filter((r) => !WT.includes(r.key)).reduce((sum, r) => sum + (r.unit || 0) * r.qty, 0);
      const trade = Math.round(tradeBase * this.tradeRate * 100) / 100;
      const discount = Math.max(kit, trade);
      const isTrade = trade > kit;
      const name = !discount ? '' : (isTrade ? `Trade savings (${Math.round(this.tradeRate * 100)}%)` : 'Complete kit savings');
      return { subtotal, discount, total: subtotal - discount, name, isTrade };
    }

    missing() {
      const s = this.state;
      if (s.fuel !== 'gas') return [];
      return [!s.burner && 'burner', !s.ignition && 'ignition', s.logs === 'ceramic' && !s.logstyle && 'ceramic log style'].filter(Boolean);
    }

    /* ---------- markup built once from the model config ---------- */
    buildStatic() {
      const cfg = this.cfg;
      this.$('[data-fdp-sizes]').innerHTML = cfg.sizes.map((s) =>
        `<button type="button" data-size="${esc(s.key)}" class="choice" aria-pressed="false"><b>${esc(s.label)}</b><small>${esc(s.dims)}</small><i aria-hidden="true">✓</i></button>`).join('');
      this.$('[data-fdp-ignitions]').innerHTML = cfg.ignition.map((ig) =>
        `<button type="button" data-ignition="${esc(ig.label)}" class="choice" aria-pressed="false"><span class="ignition-label"><b>${esc(ig.label)}</b>${ICONS[ig.icon] || ''}${ig.info ? `<span class="fdp-info" role="button" tabindex="0" data-fdp-tip="${esc(ig.label)}" aria-label="What is ${esc(ig.label)}?">i</span>` : ''}</span><small data-fdp-ig-price="${esc(ig.label)}"></small><i aria-hidden="true">✓</i></button>`).join('');
      this.$('[data-fdp-media]').innerHTML = cfg.media.map((m) =>
        `<button type="button" data-media="${esc(m.label)}" class="choice illustrated" aria-pressed="false">${this.img(m.image, m.label)}<b>${esc(m.label)}</b><small data-fdp-media-qty="${esc(m.label)}"></small><i aria-hidden="true">✓</i></button>`).join('');
      this.$('[data-fdp-logs]').innerHTML =
        `<button type="button" data-logs="ceramic" class="choice illustrated" aria-pressed="false">${this.img(cfg.ceramic.image, cfg.ceramic.label)}<b>${esc(cfg.ceramic.label)}</b><small id="ceramicQuantity" data-fdp-ceramic-price></small><i aria-hidden="true">✓</i></button>` +
        `<button type="button" data-logs="steel" class="choice illustrated" aria-pressed="false">${this.img(cfg.steel_logs_image, 'Steel welded log set')}<b>Steel Welded Log Set</b><small id="steelPrice" data-fdp-steel-price></small><i aria-hidden="true">✓</i></button>`;
      this.$('[data-fdp-logstyles]').innerHTML = cfg.ceramic.styles.map((st) =>
        `<button type="button" data-logstyle="${esc(st.label)}" class="choice illustrated" aria-pressed="false">${this.img(st.image, st.label + ' ceramic logs')}<b>${esc(st.label)}</b><i aria-hidden="true">✓</i></button>`).join('');
      const incCards = (cfg.included || []).filter((inc) => inc.image).map((inc, i) =>
        `<div class="fdp-included-card" data-fdp-inc-row="${i}"${inc.wood_only ? ' data-wood-only' : ''}><div class="accessory-image-frame">${this.img(inc.image, inc.label)}</div><span><b>${esc(inc.label)}</b><small>${esc(inc.note || '')}</small></span><em class="fdp-included-tag">Included</em></div>`).join('');
      this.$('[data-fdp-accessories]').innerHTML = incCards + this.accKeys().map((k) => {
        const info = cfg.accessory_info[k];
        return `<label data-fdp-acc-row="${k}"><div class="accessory-image-frame">${this.img(info.image, info.label)}</div><span><b>${esc(info.label)}${ACC_DETAILS[k] ? ` <span class="fdp-info fdp-acc-info" role="button" tabindex="0" data-fdp-acc-tip="${k}" aria-label="Details about the ${esc(info.label)}">i</span>` : ''}</b><small>${esc(info.note)}</small><em class="accessory-price" data-fdp-acc-price="${k}"></em></span><input type="checkbox" value="${k}" data-fdp-acc="${k}"></label>`;
      }).join('');
      // Fuel photos: config image > a product photo whose alt text mentions "gas" > featured photo
      const media = (this.product.media || []).filter((m) => m.media_type === 'image');
      const gasMedia = media.find((m) => /\bgas\b/i.test(m.alt || ''));
      const wImg = this.$('[data-fdp-fuel-img="wood"]'), gImg = this.$('[data-fdp-fuel-img="gas"]');
      if (cfg.images && cfg.images.wood) wImg.src = cfg.images.wood;
      if (cfg.images && cfg.images.gas) gImg.src = cfg.images.gas;
      else if (gasMedia) gImg.src = gasMedia.src + (gasMedia.src.includes('?') ? '&' : '?') + 'width=700';
      this.updateFromPrices();
    }

    /** "From $X" on the fuel cards; re-run once live prices load. */
    updateFromPrices() {
      const cfg = this.cfg;
      const minOf = (arr) => Math.min(...arr.filter((n) => typeof n === 'number'));
      const woodFrom = minOf(cfg.sizes.map((s) => this.vessel('wood', s).price));
      const gasFrom = minOf(cfg.sizes.map((s) => (this.vessel('gas', s).price || 0) + minOf(s.burners.map((b) => this.item(b).price))));
      this.$('[data-fdp-from="wood"]').textContent = `From ${money(woodFrom)}`;
      this.$('[data-fdp-from="gas"]').textContent = `From ${money(gasFrom)}`;
    }

    /** Accessory photo: size-specific (e.g. the 60" grate) if set, else the model default. */
    accImage(k) { const e = this.size().accessories[k]; return (e && e.image) || this.cfg.accessory_info[k].image || null; }

    img(src, alt) {
      return src ? `<img src="${esc(src)}" alt="${esc(alt)}" loading="lazy" nopin="nopin" data-pin-nopin="true">`
        : `<span class="fdp-img-placeholder" role="img" aria-label="${esc(alt)} photo coming soon">Photo coming soon</span>`;
    }

    /* ---------- events ---------- */
    /** Small explainer pop-up (ignition types for now). */
    openAccTip(k) {
      const info = this.cfg.accessory_info[k], d = this.$('[data-fdp-tip-dialog]');
      if (!info || !d || !ACC_DETAILS[k]) return;
      this.$('[data-fdp-tip-title]').textContent = info.label;
      this.$('[data-fdp-tip-text]').innerHTML = `<ul class="fdp-tip-list">${ACC_DETAILS[k].map((t) => `<li>${esc(t)}</li>`).join('')}</ul>`;
      const box = this.$('[data-fdp-tip-img]'), src = this.accImage(k);
      const pics = [[src, info.label + (info.image2 ? ' (logo side)' : '')], [info.image2, info.label + ' (silver side)']].filter((p) => p[0]);
      box.innerHTML = pics.map((p) => `<img src="${esc(p[0])}" alt="${esc(p[1])}">`).join('');
      box.classList.toggle('fdp-tip-img--pair', pics.length > 1);
      box.hidden = !box.innerHTML;
      if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', '');
    }

    /** "What's in the box" for the CrossFire burners (two kit sizes). */
    openBurnerTip() {
      const d = this.$('[data-fdp-tip-dialog]');
      if (!d) return;
      const kits = [
        ['Burners up to 249,000 BTU', ['Brass burner', 'Aluminum burner plate', 'One 36″ × ¾″ flexible gas line', 'Two ¾″ flared fittings', 'A ¾″ key valve for gas shutoff and flame adjustment', '3″ and 12″ operating keys', 'One ½″ × 2″ threaded pipe nipple']],
        ['Burners from 250,000–420,000 BTU', ['Brass burner', 'Aluminum burner plate', 'Two 36″ × ¾″ flexible gas lines', 'Four ¾″ flared fittings', 'A ¾″ key valve for gas shutoff and flame adjustment', '3″ and 12″ operating keys', 'Two ¾″ × 3″ threaded pipe nipples', 'Two ¾″ tee fittings', 'One ¾″ × ½″ male-to-male reducing fitting']]
      ];
      this.$('[data-fdp-tip-title]').textContent = 'What comes with your burner';
      this.$('[data-fdp-tip-text]').innerHTML = kits.map(([h, items]) => `<p class="fdp-tip-sub">${esc(h)}</p><ul class="fdp-tip-list">${items.map((t) => `<li>${esc(t)}</li>`).join('')}</ul>`).join('');
      const box = this.$('[data-fdp-tip-img]');
      box.innerHTML = ''; box.hidden = true; box.classList.remove('fdp-tip-img--pair');
      if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', '');
    }

    /** Plain-text explainer for "Choose Your Fire Media". */
    openMediaTip() {
      const d = this.$('[data-fdp-tip-dialog]');
      if (!d) return;
      this.$('[data-fdp-tip-title]').textContent = 'What is fire media?';
      this.$('[data-fdp-tip-text]').textContent = 'Fire media is the decorative rock that covers the burner plate and surrounds the burner for a finished look. Choose black lava rock or gray rolled rock to complement your outdoor space.';
      const t = this.$('[data-fdp-media-tip]') || {}, ds = t.dataset || {};
      const pics = [[ds.rockLava, 'Black lava rock'], [ds.rockGrey, 'Gray rolled rock']].filter((p) => p[0]);
      const box = this.$('[data-fdp-tip-img]');
      box.innerHTML = pics.map((p) => `<figure class="fdp-tip-fig"><img src="${esc(p[0])}" alt="${esc(p[1])}"><figcaption>${esc(p[1])}</figcaption></figure>`).join('');
      box.classList.toggle('fdp-tip-img--pair', pics.length > 1);
      box.hidden = !pics.length;
      if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', '');
    }

    openTip(label) {
      const ig = this.cfg.ignition.find((x) => x.label === label);
      const d = this.$('[data-fdp-tip-dialog]');
      if (!ig || !d) return;
      this.$('[data-fdp-tip-title]').textContent = ig.label;
      this.$('[data-fdp-tip-text]').textContent = ig.info;
      // Photo if we have one, otherwise the option's own icon (e.g. Electronic Ignition)
      const box = this.$('[data-fdp-tip-img]');
      box.innerHTML = ig.image ? `<img src="${esc(ig.image)}" alt="${esc(ig.label)}">` : (ICONS[ig.icon] ? `<span class="fdp-tip-icon" aria-hidden="true">${ICONS[ig.icon]}</span>` : '');
      box.hidden = !box.innerHTML;
      if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', '');
    }

    bind() {
      this.on(this, 'click', (e) => {
        if (e.target.closest('[data-fdp-media-tip]')) { e.preventDefault(); e.stopPropagation(); this.openMediaTip(); return; }
        if (e.target.closest('[data-fdp-burner-tip]')) { e.preventDefault(); e.stopPropagation(); this.openBurnerTip(); return; }
        const accTip = e.target.closest('[data-fdp-acc-tip]');
        if (accTip) { e.preventDefault(); e.stopPropagation(); this.openAccTip(accTip.dataset.fdpAccTip); return; }
        const tip = e.target.closest('[data-fdp-tip]');
        if (tip) { e.preventDefault(); e.stopPropagation(); this.openTip(tip.dataset.fdpTip); return; }
        if (e.target.closest('[data-fdp-tip-close]')) { this.$('[data-fdp-tip-dialog]').close(); return; }
        if (e.target.matches('[data-fdp-tip-dialog]')) { e.target.close(); return; }
        const b = e.target.closest('button');
        if (!b || !this.contains(b)) return;
        const s = this.state;
        for (const kind of KINDS) {
          if (b.dataset[kind] !== undefined) {
            s[kind] = b.dataset[kind];
            if (kind === 'size' && s.burner && !this.size().burners.some((x) => x.label === s.burner)) s.burner = null;
            if (kind === 'logs' && s.logs !== 'ceramic') s.logstyle = null;
            if (kind === 'fuel' && s.fuel === 'gas') s.acc.delete('grate');
            this.render();
            return;
          }
        }
        if (b.dataset.clear) { s[b.dataset.clear] = null; if (b.dataset.clear === 'logs') s.logstyle = null; this.render(); return; }
        if (b.dataset.fdpThumb !== undefined || b.dataset.fdpModel !== undefined) { this.showPhoto(b); return; }
        if (b.dataset.fdpPhotoStep !== undefined) { this.stepPhoto(Number(b.dataset.fdpPhotoStep)); return; }
        if (b.hasAttribute('data-fdp-add')) { this.addToCart(); return; }
        if (b.hasAttribute('data-fdp-upsell-close')) { this.$('[data-fdp-upsell]').close(); return; }
        if (b.dataset.fdpOffer !== undefined) this.addOffer(b);
      });
      this.on(this, 'keydown', (e) => {
        const tip = e.target.closest && e.target.closest('[data-fdp-tip]');
        if (tip && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); this.openTip(tip.dataset.fdpTip); }
        const mt = e.target.closest && e.target.closest('[data-fdp-media-tip]');
        if (mt && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); this.openMediaTip(); return; }
        const bt = e.target.closest && e.target.closest('[data-fdp-burner-tip]');
        if (bt && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); this.openBurnerTip(); return; }
        const at = e.target.closest && e.target.closest('[data-fdp-acc-tip]');
        if (at && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); this.openAccTip(at.dataset.fdpAccTip); }
      });
      this.on(this, 'change', (e) => {
        const t = e.target, s = this.state;
        if (t.matches('[data-fdp-acc]')) {
          const k = t.value;
          if (t.checked) { s.acc.add(k); if (LIDS.includes(k)) LIDS.filter((x) => x !== k).forEach((x) => s.acc.delete(x)); }
          else s.acc.delete(k);
          this.render();
        }
        if (t.matches('[data-fdp-kit-toggle]')) {
          const lid = s.acc.has('lid_stainless') ? 'lid_stainless' : 'lid_corten';
          if (t.checked) { s.acc.add(lid); s.acc.add('grate'); s.acc.add('cover'); }
          else { LIDS.forEach((x) => s.acc.delete(x)); s.acc.delete('grate'); s.acc.delete('cover'); }
          this.render();
        }
      });
      const dlg = this.$('[data-fdp-upsell]');
      this.on(dlg, 'click', (e) => { if (e.target === dlg) dlg.close(); });
    }

    /** Loads a gallery photo at the size the hero will use; cached so each one only downloads once. */
    loadPhoto(btn) {
      this.photoCache = this.photoCache || new Map();
      const key = btn.dataset.fdpThumb;
      if (!this.photoCache.has(key)) {
        const im = new Image();
        im.sizes = '(min-width: 801px) 52vw, 100vw';
        if (btn.dataset.srcset) im.srcset = btn.dataset.srcset;
        const ready = new Promise((r) => { im.onload = r; im.onerror = r; });
        im.src = key;
        this.photoCache.set(key, ready);
      }
      return this.photoCache.get(key);
    }

    /** Quietly fetch the rest of the gallery once the page has settled, so thumbnail clicks are instant. */
    /** Mouse wheel / trackpad scrolls the thumbnail strip sideways, smoothly. */
    setupThumbStrip() {
      const strip = this.$('.thumbs');
      if (!strip) return;
      // Vertical mouse wheel scrolls the strip sideways with one eased animation (no stutter from
      // stacking smooth-scroll calls). Native horizontal trackpad swipes are left to the browser.
      let target = null, raf = 0;
      const max = () => strip.scrollWidth - strip.clientWidth;
      const stop = () => { if (raf) cancelAnimationFrame(raf); raf = 0; target = null; };
      const tick = () => {
        if (target === null) { raf = 0; return; } // a swipe or click took over: don't pull the strip back
        const diff = target - strip.scrollLeft;
        if (Math.abs(diff) < 0.5) { strip.scrollLeft = target; target = null; raf = 0; return; }
        strip.scrollLeft += diff * 0.22;
        raf = requestAnimationFrame(tick);
      };
      this.on(strip, 'wheel', (e) => {
        if (max() <= 0) return;
        if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) { stop(); return; } // horizontal swipe: native
        const step = e.deltaMode === 1 ? e.deltaY * 40 : e.deltaMode === 2 ? e.deltaY * strip.clientWidth : e.deltaY;
        const from = target === null ? strip.scrollLeft : target;
        const next = Math.max(0, Math.min(max(), from + step));
        if (next === from && (from <= 0 || from >= max())) return; // at an end: let the page scroll
        e.preventDefault();
        target = next;
        if (!raf) raf = requestAnimationFrame(tick);
      }, { passive: false });
      // Any direct drag or touch on the strip hands control back to the browser
      this.on(strip, 'pointerdown', stop);
      this.on(strip, 'touchstart', stop, { passive: true });
      this.stopThumbScroll = stop;
    }

    /** Arrow buttons on the main photo: step through the thumbnails, looping at the ends. */
    stepPhoto(dir) {
      const all = this.$$('.thumbs button');
      if (!all.length) return;
      const cur = Math.max(0, all.findIndex((x) => x.classList.contains('active')));
      this.showPhoto(all[(cur + dir + all.length) % all.length]);
    }

    /** Swipe left/right on the main photo (phones). */
    setupPhotoSwipe() {
      const hero = this.$('.hero');
      if (!hero) return;
      // Pointer events cover touch swipes and mouse drags on desktop
      let x0 = null, y0 = null;
      this.on(hero, 'pointerdown', (e) => { if (e.button !== 0 || (e.target.closest && e.target.closest('.fdp-model'))) return; x0 = e.clientX; y0 = e.clientY; hero.classList.add('is-dragging'); });
      const end = (e) => {
        hero.classList.remove('is-dragging');
        if (x0 === null) return;
        const dx = e.clientX - x0, dy = e.clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) this.stepPhoto(dx < 0 ? 1 : -1);
      };
      this.on(hero, 'pointerup', end);
      this.on(hero, 'pointercancel', () => { x0 = null; hero.classList.remove('is-dragging'); });
      this.on(hero, 'pointerleave', (e) => { if (x0 !== null) end(e); });
      this.on(hero, 'dragstart', (e) => e.preventDefault());
    }

    preloadGallery() {
      const run = () => this.$$('.thumbs button').forEach((b, i) => { if (i > 0 && b.dataset.fdpThumb) this.loadPhoto(b); });
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2500 }); else setTimeout(run, 1200);
    }

    async showPhoto(btn) {
      const hero = this.$('[data-fdp-hero]');
      const all = this.$$('.thumbs button');
      all.forEach((x) => x.classList.toggle('active', x === btn));
      this.photoWant = btn;
      // Keep the active thumbnail in view by scrolling the strip sideways only (never the page)
      const strip = this.$('.thumbs');
      if (strip) {
        if (this.stopThumbScroll) this.stopThumbScroll();
        const r = btn.getBoundingClientRect(), sr = strip.getBoundingClientRect();
        // Slide the strip so the chosen thumbnail sits second from the left, revealing the photos after it
        const all2 = this.$$('.thumbs button'), idx = all2.indexOf(btn);
        const prev = idx > 0 ? all2[idx - 1] : btn;
        const target = strip.scrollLeft + (prev.getBoundingClientRect().left - sr.left);
        strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      }
      // 3D model: hide the photo, show (and load on first use) Shopify's model viewer
      const modelId = btn.dataset.fdpModel;
      this.$$('[data-fdp-model-view]').forEach((v) => { v.hidden = v.dataset.fdpModelView !== modelId; });
      if (hero) hero.hidden = !!modelId;
      if (modelId) {
        if (!this.modelViewerLoading && window.Shopify && Shopify.loadFeatures && !customElements.get('model-viewer')) {
          this.modelViewerLoading = true;
          Shopify.loadFeatures([{ name: 'model-viewer', version: '1.12', onLoad: () => {} }]);
        }
      }
      if (hero && !modelId) {
        hero.classList.add('is-swapping');
        // Swap once the photo has loaded (usually instant after preloading); never wait more than 1.2s
        await Promise.race([this.loadPhoto(btn), new Promise((r) => setTimeout(r, 1200))]);
        if (this.photoWant !== btn) return; // a later click won
        hero.sizes = '(min-width: 801px) 52vw, 100vw';
        hero.srcset = btn.dataset.srcset || ''; hero.src = btn.dataset.fdpThumb; hero.alt = btn.dataset.alt;
        const done = () => hero.classList.remove('is-swapping');
        if (hero.complete) done(); else { hero.addEventListener('load', done, { once: true }); hero.addEventListener('error', done, { once: true }); }
      }
      all.forEach((x) => x.classList.toggle('active', x === btn));
      const i = all.indexOf(btn) + 1;
      const tag = this.$('[data-fdp-photo-tag]');
      if (tag) tag.hidden = !/-vents?\b/i.test(btn.dataset.fdpThumb || '');
      const count = this.$('[data-fdp-photo-count]');
      if (count) count.textContent = `Photo ${i} of ${all.length}`;
    }

    /* ---------- render ---------- */
    render() {
      const s = this.state, size = this.size(), gas = s.fuel === 'gas', cfg = this.cfg;
      const rows = this.lines(), p = this.pricing(rows), miss = this.missing();

      // Burners depend on size
      const burnerBox = this.$('[data-fdp-burners]');
      if (burnerBox.dataset.size !== size.key) {
        burnerBox.dataset.size = size.key;
        burnerBox.innerHTML = size.burners.map((b) =>
          `<button type="button" data-burner="${esc(b.label)}" class="choice" aria-pressed="false"><span class="burner-label">${this.dataset.fdpFlame ? `<img class="fdp-wt-flame" src="${this.dataset.fdpFlame}" alt="" width="12" height="32">` : ICONS.flame}<b>${esc(b.label)}</b></span><span class="burner-coverage">${esc(b.coverage || '')}</span><small data-fdp-burner-price></small><i aria-hidden="true">✓</i></button>`).join('');
      }
      this.$$('[data-burner]').forEach((b) => {
        const entry = size.burners.find((x) => x.label === b.dataset.burner);
        b.querySelector('[data-fdp-burner-price]').textContent = this.item(entry).price == null ? 'Price pending' : money(this.item(entry).price);
      });

      for (const kind of KINDS) {
        this.$$(`[data-${kind}]`).forEach((b) => { const on = b.dataset[kind] === s[kind]; b.classList.toggle('selected', on); b.setAttribute('aria-pressed', String(on)); });
      }
      this.$$('[data-clear]').forEach((b) => { const on = !s[b.dataset.clear]; b.classList.toggle('selected', on); b.setAttribute('aria-pressed', String(on)); });

      cfg.ignition.forEach((ig) => {
        const el = this.$(`[data-fdp-ig-price="${CSS.escape(ig.label)}"]`);
        if (el) el.textContent = ig.price === 0 && !ig.product ? 'Included' : `+${money(this.item(ig).price)}`;
      });
      cfg.media.forEach((m) => {
        const el = this.$(`[data-fdp-media-qty="${CSS.escape(m.label)}"]`), unit = this.item(m).price;
        if (el) el.textContent = `${m.bag} · ${size.media_bags} bags × ${money(unit)} = ${money(unit * size.media_bags)}`;
      });
      const cer = this.item({ product: cfg.ceramic.product, variant: cfg.ceramic.styles[0].variant, price: cfg.ceramic.price }).price;
      this.$('[data-fdp-ceramic-price]').textContent = `${size.ceramic_sets} ${size.ceramic_sets === 1 ? 'set' : 'sets'} · +${money(cer * size.ceramic_sets)}`;
      this.$('[data-fdp-steel-price]').textContent = `+${money(this.item(size.steel_logs).price)}`;
      this.$('[data-fdp-ceramic-styles]').hidden = s.logs !== 'ceramic';

      // Gas / wood
      this.$('[data-fdp-gas]').hidden = !gas;
      this.$('[data-fdp-install]').hidden = !gas;
      this.$('[data-fdp-accessory-step]').textContent = gas ? '7' : '3';
      const stepCount = this.$('[data-fdp-step-count]'); if (stepCount) stepCount.textContent = gas ? '7 steps · burner and ignition required' : '3 quick steps';

      // Accessories (the step hides when a model offers none, e.g. the Pierson)
      const accStep = this.$('[data-fdp-step="accessories"]');
      if (accStep) accStep.hidden = this.accKeys().length === 0;
      this.$$('[data-fdp-inc-row][data-wood-only]').forEach((r) => { r.hidden = gas; });
      this.accKeys().forEach((k) => {
        const row = this.$(`[data-fdp-acc-row="${k}"]`), input = row.querySelector('input');
        const hide = gas && cfg.accessory_info[k].wood_only;
        row.hidden = hide; input.disabled = hide;
        input.checked = s.acc.has(k) && !hide;
        row.classList.toggle('selected', input.checked);
        row.querySelector('[data-fdp-acc-price]').textContent = `+${money(this.accessory(k).price)}`;
        const frame = row.querySelector('.accessory-image-frame'), src = this.accImage(k);
        if (frame.dataset.src !== String(src)) { frame.dataset.src = String(src); frame.innerHTML = this.img(src, cfg.accessory_info[k].label); }
      });

      // Kit card with strike-through value (models without a kit hide it)
      const kitBox = this.$('[data-fdp-kit]');
      if (!cfg.kit) { kitBox.hidden = true; } else {
      const kitOn = this.kitEligible(), stainless = s.acc.has('lid_stainless');
      const lidKey = stainless ? 'lid_stainless' : 'lid_corten';
      const kitValue = ['grate', 'cover'].reduce((sum, k) => sum + (this.accessory(k).price || 0), this.accessory(lidKey).price || 0);
      kitBox.hidden = gas;
      kitBox.classList.toggle('selected', kitOn);
      this.$('[data-fdp-kit-toggle]').checked = kitOn;
      this.$('[data-fdp-kit-saving]').textContent = cfg.kit.discount;
      this.$('[data-fdp-kit-price]').innerHTML = `${kitOn ? 'Complete kit selected' : 'Add the complete kit'} · <s>${money(kitValue)}</s> <b>+${money(kitValue - cfg.kit.discount)}</b>`;
      this.$('[data-fdp-kit-thumbs]').innerHTML = [lidKey, 'grate', 'cover'].map((k) => this.accImage(k) ? `<img src="${esc(this.accImage(k))}" alt="">` : '').join('');
      const st = this.$('[data-fdp-kit-status]');
      st.hidden = !kitOn;
      st.textContent = p.isTrade ? 'Complete kit selected — your trade price gives you greater savings.' : `Complete kit selected — $${cfg.kit.discount} savings applied.`;

      }

      // Summary
      this.$('[data-fdp-summary-title]').textContent = `${cfg.model} ${size.key} · ${gas ? 'Gas' : 'Wood-Burning'}`;
      this.$('[data-fdp-summary-dims]').textContent = size.dims;
      this.$('[data-fdp-breakdown]').innerHTML = rows.map((r) => `<div class="cart-row"><div><b>${esc(r.label)}</b><small>${r.qty > 1 && r.unit != null ? `${r.qty} × ${money(r.unit)} each` : `Quantity ${r.qty}`}</small></div><span>${r.included ? 'Included' : r.unit == null ? 'Price pending' : money(r.unit * r.qty)}</span></div>`).join('') +
        (p.discount ? `<div class="cart-row cart-savings"><div><b>${esc(p.name)}</b><small>${p.isTrade ? 'On Frontier Designs products · offers do not combine' : 'Lid + grate + cover'}</small></div><span>−${money(p.discount)}</span></div>` : '');
      const pendingRows = rows.filter((r) => r.unit == null && !r.included);
      // Gas with no burner yet: show the complete "from" price (cheapest burner for this size), not the lower vessel-only figure
      const needBurner = gas && !s.burner;
      const burnerPrices = needBurner ? (size.burners || []).map((b) => this.item(b).price).filter((n) => typeof n === 'number') : [];
      const totalText = needBurner && burnerPrices.length
        ? `From ${money(p.total + Math.min(...burnerPrices))}`
        : money(p.total) + (pendingRows.length ? '+' : '');
      this.$('[data-fdp-total]').textContent = totalText;
      this.$('[data-fdp-top-price]').textContent = totalText;
      // Affirm line (Rich's mockup): monthly example only for the base setup
      const affMsg = this.$('[data-fdp-affirm-msg]');
      if (affMsg) {
        const base = s.fuel === 'wood' && s.size === cfg.sizes[0].key && s.acc.size === 0;
        const monthly = p.total / 24;
        affMsg.textContent = base ? `Starting at ${money(Math.round(monthly))}/month or 0% APR with` : 'Pay over time, from 0% APR with';
        const ex = this.$('[data-fdp-affirm-example]');
        if (ex) ex.textContent = `Illustrative example: a ${money(p.total)} purchase over 24 months at 0% APR is $${monthly.toFixed(2)} per month${base ? `, shown rounded to ${money(Math.round(monthly))}` : ''}.`;
      }
      this.$('[data-fdp-price-note]').textContent = needBurner
        ? `Gas · ${size.label} · choose a burner to see your total`
        : `${gas ? 'Gas' : 'Wood-burning'} · ${size.label}${rows.filter((r) => !r.included).length > 1 ? ' · as configured' : ''}`;
      this.$('[data-fdp-sticky-total]').textContent = totalText;
      this.$('[data-fdp-policy]').hidden = gas;

      // Next-step highlight + Add to Cart state
      const nextStep = gas ? (!s.burner ? 'burner' : !s.ignition ? 'ignition' : (s.logs === 'ceramic' && !s.logstyle) ? 'logs' : null) : null;
      this.$$('[data-fdp-step]').forEach((f) => f.classList.toggle('is-next', f.dataset.fdpStep === nextStep));
      const unmapped = rows.filter((r) => !r.mapped);
      const soldOut = rows.filter((r) => r.mapped && !r.available && !r.included);
      const blocked = miss.length > 0 || unmapped.length > 0 || soldOut.length > 0 || this.pending;
      this.$$('[data-fdp-add]').forEach((b) => { b.disabled = blocked; b.textContent = this.pending ? 'ADDING…' : 'ADD TO CART'; });
      let foot = 'Prices update with your selections.';
      if (miss.length) foot = `Choose your ${miss.join(' and ')} to continue.`;
      else if (soldOut.length) foot = `${soldOut.map((r) => r.label).join(', ')} ${soldOut.length > 1 ? 'are' : 'is'} currently unavailable. Call ${this.phone} and we’ll help.`;
      else if (pendingRows.length) foot = `Pricing for the ${pendingRows.map((r) => r.label).join(', ')} is coming soon. Call ${this.phone} for a quote.`;
      else if (unmapped.length) foot = '';
      this.$('[data-fdp-foot]').textContent = foot;

      // Information panel copy
      this.$('[data-fdp-dimensions]').innerHTML = cfg.sizes.map((z) =>
        `<dt>${esc(cfg.model)} ${esc(z.key)}</dt><dd>${esc(z.dims)}</dd><dt>Weight</dt><dd>${esc(z.weight ? `${z.weight.wood} wood · ${z.weight.gas} gas` : '—')}</dd>`).join('');
      this.$('[data-fdp-care-title]').textContent = gas ? 'Gas installation & use' : 'Placement & use';
      this.$('[data-fdp-care-copy]').textContent = gas
        ? 'A licensed professional must confirm the correct natural gas or propane configuration, connections, clearances, and installation against the selected system’s instructions. Frontier Designs does not provide installation.'
        : 'Use on a suitable non-combustible surface and follow the product’s placement requirements and local fire regulations. Monitor the fire and fully extinguish flames and embers after use.';
      this.$('[data-fdp-lead-time]').textContent = gas ? 'Gas fire pits ship in approximately 4 weeks.' : '';
    }

    /* ---------- cart ---------- */
    /** Scroll to the first required step that still needs a choice and flash it. */
    goToMissing() {
      const m = this.missing()[0];
      const key = m === 'ceramic log style' ? 'logs' : m;
      const step = this.$(`[data-fdp-step="${key}"]`);
      if (!step) return;
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 80;
      window.scrollTo({ top: step.getBoundingClientRect().top + window.scrollY - header - 16, behavior: 'smooth' });
      step.classList.remove('is-flash'); void step.offsetWidth; step.classList.add('is-flash');
    }

    async addToCart() {
      if (this.pending || this.missing().length) return;
      const all = this.lines();
      const rows = all.filter((r) => r.id);
      if (!rows.length || all.some((r) => !r.mapped)) return;
      const group = `fdp-${Date.now()}`;
      const s = this.state, size = this.size();
      const setup = [`${this.cfg.model} ${size.key}`, s.fuel === 'gas' ? 'Gas' : 'Wood-burning']
        .concat(all.filter((r) => !r.vessel).map((r) => (r.qty > 1 ? `${r.label} ×${r.qty}` : r.label))).join(' · ');
      const items = rows.map((r) => ({ id: r.id, quantity: r.qty, properties: r.vessel ? { 'Your setup': setup, _fdp_group: group } : { _fdp_group: group } }));
      const ok = await this.postItems(items);
      if (ok) this.openUpsell();
    }

    async postItems(items) {
      this.pending = true; this.render();
      const err = this.$('[data-fdp-error]'); err.hidden = true;
      const addUrl = ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';
      try {
        const r = await fetch(addUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: 'cart-icon-bubble', sections_url: window.location.pathname })
        });
        const data = await r.json();
        if (!r.ok || data.status) throw new Error(data.description || data.message || 'Something went wrong adding to cart.');
        this.updateBubble(data.sections && data.sections['cart-icon-bubble']);
        // Signed-in trade customers: apply their tier's code (TRADE15/20/25) to the cart automatically.
        // The code itself only works for that tier's tagged customers and only on Frontier Designs products.
        if (this.dataset.tradeCode) {
          try {
            await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ discount: this.dataset.tradeCode }) });
          } catch (e) { /* checkout still accepts the code manually */ }
          try { await fetch(`/discount/${encodeURIComponent(this.dataset.tradeCode)}`, { credentials: 'same-origin' }); } catch (e) { /* ignore */ }
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'fdp-configurator', cartData: data });
        return true;
      } catch (e) {
        err.textContent = `${e.message} Your selections are saved. Please try again or call ${this.phone}.`;
        err.hidden = false;
        return false;
      } finally {
        this.pending = false; this.render();
      }
    }

    updateBubble(html) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble || !html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const src = doc.querySelector('.shopify-section');
      if (src) bubble.innerHTML = src.innerHTML;
    }

    offers() {
      const s = this.state, out = [];
      if (this.cfg.kit && s.fuel === 'wood' && !this.kitEligible()) {
        const missing = ['lid_corten', 'grate', 'cover'].filter((k) => (k === 'lid_corten' ? !LIDS.some((l) => s.acc.has(l)) : !s.acc.has(k)));
        const parts = missing.map((k) => ({ k, it: this.accessory(k) }));
        if (parts.length && parts.every((x) => x.it.id && x.it.available)) {
          const value = parts.reduce((sum, x) => sum + x.it.price, 0);
          out.push({ label: 'Complete the Wood-Burning Kit', note: `Adds ${parts.map((x) => this.cfg.accessory_info[x.k].label).join(', ')}. Kit savings of $${this.cfg.kit.discount} apply at checkout.`, price: value, items: parts.map((x) => ({ id: x.it.id, quantity: 1 })), image: this.accImage('lid_corten') });
        }
      }
      if (this.accKeys().includes('deck') && !s.acc.has('deck')) {
        const it = this.accessory('deck');
        if (it.id && it.available) out.push({ label: 'Deck Protector', note: this.cfg.accessory_info.deck.note, price: it.price, items: [{ id: it.id, quantity: 1 }], image: this.cfg.accessory_info.deck.image });
      }
      (this.cfg.upsell || []).forEach((u) => {
        const it = this.item(u);
        if (it.id && it.available) out.push({ label: u.label, note: u.note, price: it.price, items: [{ id: it.id, quantity: 1 }], image: u.image });
      });
      return out;
    }

    openUpsell() {
      const dlg = this.$('[data-fdp-upsell]');
      this._offers = this.offers();
      this.$('[data-fdp-upsell-title]').textContent = `Your ${this.cfg.model} is in the cart.`;
      this.$('[data-fdp-upsell-offers]').innerHTML = this._offers.length
        ? '<p class="fdp-upsell-lead">Complete your setup</p>' + this._offers.map((o, i) => `<div class="fdp-offer">${o.image ? `<img src="${esc(o.image)}" alt="">` : ''}<div><b>${esc(o.label)}</b><small>${esc(o.note)}</small></div><button type="button" class="fdp-offer-add" data-fdp-offer="${i}">Add · ${money(o.price)}</button></div>`).join('')
        : '';
      if (typeof dlg.showModal === 'function') dlg.showModal();
    }

    async addOffer(btn) {
      const o = this._offers && this._offers[+btn.dataset.fdpOffer];
      if (!o || btn.disabled) return;
      btn.disabled = true; btn.textContent = 'Adding…';
      const ok = await this.postItems(o.items);
      btn.textContent = ok ? 'Added ✓' : 'Try again';
      btn.disabled = ok;
    }

    /* ---------- layout: ported from Rich's mockup (Oct 15). Base markup is the mobile
       layout; desktop wraps the title block, financing and key benefits. ---------- */
    setupLayout() {
      const info = this.$('[data-fdp-info]'), anchor = this.$('[data-fdp-info-anchor]'), grid = this.$('.product-grid');
      const overview = this.$('[data-fdp-overview]'), conf = this.$('.configure');
      const mobile = window.matchMedia('(max-width: 800px)');
      let frame = null, restoreDesktop = null, restoreMobile = null;
      const update = () => {
        frame = null;
        if (mobile.matches) { info.style.removeProperty('--information-sticky-top'); return; }
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const vh = document.documentElement.clientHeight || window.innerHeight;
        const top = Math.min(24, vh - info.getBoundingClientRect().height - 24 - header);
        info.style.setProperty('--information-sticky-top', `${top + header}px`);
      };
      const schedule = () => { if (frame !== null) cancelAnimationFrame(frame); frame = requestAnimationFrame(update); };
      const remember = (nodes, label) => {
        const homes = new Map();
        nodes.filter(Boolean).forEach((n) => { const h = document.createComment(label); n.before(h); homes.set(n, h); });
        return homes;
      };
      const putBack = (homes) => { homes.forEach((h, n) => { h.after(n); h.remove(); }); };
      const mobileComposition = () => {
        const trade = conf.querySelector('.trade-pricing-link'), benefits = conf.querySelector('.purchase-benefits');
        const promise = conf.querySelector('.product-promise'), summary = conf.querySelector('.order-summary');
        const homes = remember([trade, benefits, promise], 'mobile home');
        if (trade) summary.before(trade);
        if (benefits) summary.before(benefits);
        if (promise) info.prepend(promise);
        return () => putBack(homes);
      };
      const desktopComposition = () => {
        const titleNodes = ['.eyebrow', 'h1', '.product-type', '.product-review-row', '.price-and-trade'].map((q) => conf.querySelector(`:scope > ${q}`));
        const payment = conf.querySelector(':scope > .payment-strip'), trade = conf.querySelector('.trade-pricing-link');
        const promise = conf.querySelector('.product-promise'), summary = conf.querySelector('.order-summary');
        const homes = remember([...titleNodes, payment, trade, promise], 'desktop home');
        const el = (tag, cls) => { const e = document.createElement(tag); e.className = cls; return e; };
        const heading = el('div', 'desktop-product-heading'), title = el('div', 'desktop-product-title');
        const utilities = el('div', 'desktop-product-utilities'), keys = el('ul', 'desktop-key-benefits');
        keys.setAttribute('aria-label', 'Product benefits');
        keys.innerHTML = '<li>Born in Austin, TX</li><li>Handmade &amp; Heavy Duty</li><li>Fast, Free Shipping</li>';
        let tradeCopy = null;
        if (trade && !trade.classList.contains('is-trade')) { tradeCopy = el('span', 'desktop-trade-copy'); tradeCopy.innerHTML = 'Trade professional? <u>Apply for pricing</u>'; trade.prepend(tradeCopy); }
        conf.prepend(heading); heading.after(utilities); utilities.after(keys); heading.appendChild(title);
        titleNodes.filter(Boolean).forEach((n) => title.appendChild(n));
        if (payment) utilities.appendChild(payment);
        if (trade) summary.before(trade);
        if (promise) info.prepend(promise);
        return () => { putBack(homes); if (tradeCopy) tradeCopy.remove(); heading.remove(); utilities.remove(); keys.remove(); };
      };
      const place = () => {
        if (mobile.matches) {
          if (restoreDesktop) { restoreDesktop(); restoreDesktop = null; }
          if (!restoreMobile) restoreMobile = mobileComposition();
          grid.appendChild(info); overview.open = false;
        } else {
          if (restoreMobile) { restoreMobile(); restoreMobile = null; }
          if (!restoreDesktop) restoreDesktop = desktopComposition();
          anchor.after(info); overview.open = true;
        }
        schedule();
      };
      this.on(mobile, 'change', place);
      this.on(info, 'toggle', schedule, true);
      this.on(window, 'resize', schedule);
      if (typeof ResizeObserver !== 'undefined') { const ro = new ResizeObserver(schedule); ro.observe(info); this.cleanups.push(() => ro.disconnect()); }
      place();
    }
  }

  customElements.define('fdp-configurator', FdpConfigurator);
})();
